/**
 * trec-svc /trec/forms — staff create, prefill, edit and send TREC forms for
 * e-signature. Mirrors services/api/trec/src/schemas/forms.py.
 *
 * Lifecycle: DRAFT → PREFILLED → SENT_FOR_SIGNATURE → SIGNED. Fields are only
 * editable while DRAFT/PREFILLED; `prefill` pulls values from a client's
 * document extractions (+ the linked MLS property row) without clobbering a
 * human edit, and `send` builds the DocuSign envelope.
 */

import type { Uuid } from "@re-platform/types";

import type { ApiClient } from "../client";

/** Mirrors trec-svc's `FormType` literal (schemas/forms.py). It had drifted: the
 *  six form types V051 and later added — the two listing agreements, the IABS,
 *  the two notices and the dual-capacity disclosure — were all sendable by the
 *  server and unnameable by the SPAs, which is why the admin form-type pickers
 *  had holes in them. */
export type FormType =
  | "residential_contract_1_to_4_family"
  | "buyer_representation"
  | "buyer_representation_agreement"
  | "exclusive_right_to_sell"
  | "exclusive_right_to_lease"
  | "iabs"
  | "home_inspection_notice"
  | "consumer_protection_notice"
  | "sellers_disclosure"
  | "dual_capacity_consent"
  | "lease_application"
  | "tenant_application"
  | "addendum"
  | "amendment";

export type FormStatus =
  "DRAFT" | "PREFILLED" | "SENT_FOR_SIGNATURE" | "SIGNED" | string;

/** Where a field value came from — drives the provenance badge. */
export type FieldSource =
  "auto_extracted" | "agent_edit" | "client_input" | "manual" | string;

export interface FieldOut {
  field_name: string;
  field_value: string | null;
  source: FieldSource;
  /** Extraction that populated an auto_extracted field, else null. */
  source_document_id: Uuid | null;
  /** Human label for the source document (filename, else doc type), else null. */
  source_document_filename: string | null;
  updated_at: string;

  /**
   * V093 template metadata, joined server-side from the row that gives this
   * field a box on the page. All optional: a form with no template, or a value
   * the template does not map, carries none of it and renders as it always did.
   */
  /** "5.A(1)(a)" — what the form prints beside this box. */
  paragraph_label?: string | null;
  /** "text" | "checkbox" | "signature" | "date_signed". */
  field_kind?: string | null;
  /** This field may not print blank. */
  is_required?: boolean;
  /**
   * Whether this field is, right now, what stops the packet being sent.
   *
   * 🔴 Do NOT re-derive this from `field_value` — the server computes it with
   * the completeness gate's own predicate, and a `!field_value` test gets two
   * cases wrong in opposite directions: `broker_county` ships the literal "NA"
   * and looks filled while the send refuses, and every election is ticked by a
   * template default and looks blank while being entirely correct.
   */
  blocks_send?: boolean;
  /**
   * What this field will ACTUALLY print — the stored value, else the template's
   * default. Render a checkbox's ticked state from THIS, not `field_value`:
   * every election is ticked by a template default and never stored, so
   * `field_value` says "not ticked" about a box that prints ticked.
   */
  effective_value?: string | null;
}

export interface FormOut {
  id: Uuid;
  client_id: Uuid;
  agent_id: Uuid | null;
  form_type: FormType;
  template_version: string;
  property_id: Uuid | null;
  showing_id: Uuid | null;
  /** The envelope this form rides on with its siblings — what a resend acts on. */
  packet_id: Uuid | null;
  status: FormStatus;
  docusign_envelope_id: string | null;
  signed_pdf_s3_key: string | null;
  created_at: string;
  updated_at: string;
  fields: FieldOut[];
  /** Street address of the linked property, for display. A seller's disclosure
   *  is about a house, and its type name alone does not say which. Null when
   *  the form has no property. */
  property_address: string | null;
  /** WHICH application these documents belong to (V089). A client with a lease
   *  listing, a sale and a purchase gets three packets — around a dozen rows —
   *  and "IABS" appears in all three. Null for packets sent before V089. */
  dossier_id: Uuid | null;
  /** The client-facing number for that dossier ("Application #3"). */
  application_number: number | null;
}

export interface FormCreateRequest {
  client_id: Uuid;
  form_type: FormType;
  /** Defaults to the current TREC-published version server-side when omitted. */
  template_version?: string;
  property_id?: Uuid | null;
  showing_id?: Uuid | null;
}

export interface FieldUpdateIn {
  field_name: string;
  field_value: string | null;
}

/** A blank template PDF the client fills in by hand (TREC 55-1 today). */
export interface BlankForm {
  form_type: string;
  template_version: string | null;
  /** Presigned S3 GET — open it, don't fetch it. */
  download_url: string;
  file_name: string;
}

export const formService = {
  /**
   * Open the blank PDF for a form the client completes themselves.
   *
   * Anchor-click rather than fetch, for the same reason as `openDocument`: the
   * presigned URL is signed for a plain browser GET, and fetching it would trip
   * CORS and hand back an opaque response we cannot do anything with.
   */
  async openBlank(client: ApiClient, formType: string): Promise<void> {
    const { download_url } = await client.request<BlankForm>(
      `/trec/blank-forms/${formType}`,
    );
    const a = window.document.createElement("a");
    a.href = download_url;
    a.rel = "noopener";
    a.target = "_blank";
    window.document.body.appendChild(a);
    a.click();
    a.remove();
  },

  list(
    client: ApiClient,
    params?: {
      client_id?: Uuid;
      status?: FormStatus;
      limit?: number;
      offset?: number;
    },
  ) {
    return client.request<FormOut[]>("/trec/forms", { query: params });
  },

  get(client: ApiClient, formId: Uuid) {
    return client.request<FormOut>(`/trec/forms/${formId}`);
  },

  create(client: ApiClient, body: FormCreateRequest) {
    return client.request<FormOut>("/trec/forms", { method: "POST", body });
  },

  /** Auto-populate fields from the client's document extractions + MLS row. */
  prefill(client: ApiClient, formId: Uuid) {
    return client.request<FormOut>(`/trec/forms/${formId}/prefill`, {
      method: "POST",
    });
  },

  /** Agent field edits (source becomes 'agent_edit'; survives re-prefill). */
  updateFields(client: ApiClient, formId: Uuid, fields: FieldUpdateIn[]) {
    return client.request<FormOut>(`/trec/forms/${formId}/fields`, {
      method: "PUT",
      body: { fields },
    });
  },

  /** Send the form to the client for e-signature via DocuSign. */
  send(client: ApiClient, formId: Uuid) {
    return client.request<FormOut>(`/trec/forms/${formId}/send`, {
      method: "POST",
    });
  },

  /**
   * Start an embedded signing session: returns a short-lived (~5 min,
   * single-use) hosted DocuSign URL. Redirect the browser to it; DocuSign
   * sends the signer back to `returnUrl` (with an `?event=...` query param)
   * when the ceremony finishes/cancels.
   */
  signingView(client: ApiClient, formId: Uuid, returnUrl: string) {
    return client.request<{ url: string }>(
      `/trec/forms/${formId}/signing-view`,
      { method: "POST", body: { return_url: returnUrl } },
    );
  },

  /**
   * Reconcile the form's signature status directly from DocuSign after the
   * embedded signing session redirects back. Flips the form to SIGNED when the
   * envelope is complete, without waiting on the (separately-configured)
   * DocuSign Connect webhook. Idempotent + safe to poll.
   */
  reconcileSignature(client: ApiClient, formId: Uuid) {
    return client.request<FormOut>(
      `/trec/forms/${formId}/reconcile-signature`,
      { method: "POST" },
    );
  },

  /** Withdraw a sent (unsigned) form — voids the envelope, reopens for edits. */
  void(client: ApiClient, formId: Uuid) {
    return client.request<FormOut>(`/trec/forms/${formId}/void`, {
      method: "POST",
    });
  },

  /** Delete a draft form that was never sent (409 once an envelope exists). */
  remove(client: ApiClient, formId: Uuid) {
    return client.request<void>(`/trec/forms/${formId}`, { method: "DELETE" });
  },
};
